import { useEffect, useState } from "react";
import { AuctionStatusBadge } from "../../components/StatusBadges";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Popover, PopoverTrigger, PopoverContent } from "../../components/ui/popover";

const API = import.meta.env.VITE_API_URL || "http://localhost:9000";

type AuctionStatusDef = {
  label: string;
  description: string;
  group: "draft" | "review" | "live" | "closed" | "post_sale";
  buyerLabel: string | null;
  sellerLabel: string | null;
};

type FikenAccountMapping = {
  id: string;
  lineType: string;
  documentType: string;
  accountCode: string;
  vatCode: string | null;
  label: string | null;
};

type BidIncrementRule = {
  from: number;
  to: number | null;
  increment: number;
};

type PlatformRules = {
  auctionStatuses: Record<string, AuctionStatusDef>;
  spreadCalculationCode: string;
  bidIncrementRules: BidIncrementRule[];
};

const LINE_TYPE_LABELS: Record<string, string> = {
  car_sale: "Bilsalg",
  car_purchase: "Bilkjøp",
  transport: "Transport",
  fee: "Gebyr",
  service: "Tjeneste",
  other: "Annet",
  bank_account: "Bankkonto",
};

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  invoice: "Faktura",
  credit_note: "Kreditnota",
};

const LINE_TYPES = Object.keys(LINE_TYPE_LABELS);
const DOCUMENT_TYPES = ["invoice", "credit_note"] as const;
type DocumentType = typeof DOCUMENT_TYPES[number];

const GROUP_LABELS: Record<string, string> = {
  draft: "Utkast",
  review: "Gjennomgang",
  live: "Aktiv",
  closed: "Avsluttet",
  post_sale: "Ettersalg",
};

const GROUPS = ["draft", "review", "live", "closed", "post_sale"] as const;

function formatNOK(amount: number) {
  return new Intl.NumberFormat("nb-NO", {
    style: "currency",
    currency: "NOK",
    maximumFractionDigits: 0,
  }).format(amount);
}

export function InnstillingerPage() {
  const [rules, setRules] = useState<PlatformRules | null>(null);
  const [mappings, setMappings] = useState<FikenAccountMapping[] | null>(null);
  const [editValues, setEditValues] = useState<Record<string, { accountCode: string; vatCode: string }>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [newMapping, setNewMapping] = useState<{ lineType: string; documentType: DocumentType; accountCode: string; vatCode: string }>({
    lineType: LINE_TYPES[0],
    documentType: DOCUMENT_TYPES[0],
    accountCode: "",
    vatCode: "",
  });
  const [addingNew, setAddingNew] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/admin/platform-rules`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => setRules(data as PlatformRules));

    fetch(`${API}/api/admin/accounting/fiken-account-mappings`, { credentials: "include" })
      .then((r) => r.json())
      .then((data: FikenAccountMapping[]) => {
        setMappings(data);
        const initial: Record<string, { accountCode: string; vatCode: string }> = {};
        for (const m of data) initial[m.id] = { accountCode: m.accountCode, vatCode: m.vatCode ?? "" };
        setEditValues(initial);
      });
  }, []);

  async function saveMapping(id: string) {
    setSavingId(id);
    const res = await fetch(`${API}/api/admin/accounting/fiken-account-mappings/${id}`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        accountCode: editValues[id]?.accountCode,
        vatCode: editValues[id]?.vatCode || undefined,
      }),
    });
    if (res.ok) {
      const updated = await res.json() as FikenAccountMapping;
      setMappings((prev) => prev?.map((m) => m.id === id ? updated : m) ?? null);
      setEditValues((prev) => ({ ...prev, [id]: { accountCode: updated.accountCode, vatCode: updated.vatCode ?? "" } }));
    }
    setSavingId(null);
  }

  async function deleteMapping(id: string) {
    await fetch(`${API}/api/admin/accounting/fiken-account-mappings/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    setMappings((prev) => prev?.filter((m) => m.id !== id) ?? null);
    setEditValues((prev) => { const next = { ...prev }; delete next[id]; return next; });
  }

  async function createMapping() {
    if (!newMapping.accountCode.trim()) return;
    setAddingNew(true);
    const res = await fetch(`${API}/api/admin/accounting/fiken-account-mappings`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...newMapping,
        documentType: newMapping.lineType === "bank_account" ? "invoice" : newMapping.documentType,
        vatCode: newMapping.lineType === "bank_account" ? undefined : (newMapping.vatCode || undefined),
      }),
    });
    if (res.ok) {
      const created = await res.json() as FikenAccountMapping;
      setMappings((prev) => {
        const filtered = prev?.filter((m) => m.id !== created.id) ?? [];
        return [...filtered, created].sort((a, b) => a.lineType.localeCompare(b.lineType) || a.documentType.localeCompare(b.documentType));
      });
      setEditValues((prev) => ({ ...prev, [created.id]: { accountCode: created.accountCode, vatCode: created.vatCode ?? "" } }));
      setNewMapping((prev) => ({ ...prev, accountCode: "", vatCode: "" }));
    }
    setAddingNew(false);
  }

  return (
    <div className="px-4 py-12 sm:px-6 lg:px-8 max-w-screen-lg mx-auto space-y-10">
      <div>
        <h1
          className="text-3xl font-black tracking-tight text-foreground"
          style={{ fontFamily: "var(--font-display)" }}
        >
          Innstillinger
        </h1>
        <p className="text-muted-foreground mt-1">Plattformregler og konfigurasjoner.</p>
      </div>

      <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800 dark:border-yellow-900 dark:bg-yellow-950 dark:text-yellow-300">
        Reglene på denne siden er definert i koden. Ønsker du å gjøre endringer, må dette kommuniseres til utvikler.
      </div>

      {/* Auction Statuses */}
      <section className="space-y-4">
        <h2 className="text-xl font-bold tracking-tight">Auksjonsstatus</h2>
        <div className="space-y-4">
          {GROUPS.map((group) => {
            const statuses = rules
              ? Object.entries(rules.auctionStatuses).filter(([, def]) => def.group === group)
              : [];
            return (
              <Card key={group}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold text-muted-foreground uppercase tracking-wider">
                    {GROUP_LABELS[group]}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y">
                    {statuses.map(([status, def]) => (
                      <div key={status} className="flex items-start gap-4 px-6 py-3">
                        <div className="w-44 shrink-0 pt-0.5">
                          <AuctionStatusBadge status={status} />
                        </div>
                        <p className="text-sm text-muted-foreground">{def.description}</p>
                      </div>
                    ))}
                    {!rules && (
                      <div className="px-6 py-3 text-sm text-muted-foreground animate-pulse">Laster...</div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Bid Increment Rules */}
      <section className="space-y-4">
        <h2 className="text-xl font-bold tracking-tight">Minimum budsøkning</h2>
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground text-left">
                  <th className="px-6 py-3 font-medium">Bud fra</th>
                  <th className="px-6 py-3 font-medium">Bud til</th>
                  <th className="px-6 py-3 font-medium">Minimum økning</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rules?.bidIncrementRules.map((rule, i) => (
                  <tr key={i}>
                    <td className="px-6 py-3 tabular-nums">{formatNOK(rule.from)}</td>
                    <td className="px-6 py-3 tabular-nums text-muted-foreground">
                      {rule.to !== null ? formatNOK(rule.to - 1) : "og over"}
                    </td>
                    <td className="px-6 py-3 font-medium tabular-nums">{formatNOK(rule.increment)}</td>
                  </tr>
                ))}
                {!rules && (
                  <tr>
                    <td colSpan={3} className="px-6 py-3 text-sm text-muted-foreground animate-pulse">
                      Laster...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </section>

      {/* Spread Rules */}
      <section className="space-y-4">
        <h2 className="text-xl font-bold tracking-tight">Budspread</h2>
        <Card>
          <CardContent className="p-6">
            {rules ? (
              <pre className="overflow-x-auto whitespace-pre-wrap rounded-md border bg-muted/40 p-4 text-xs leading-6 text-foreground">
                <code>{rules.spreadCalculationCode}</code>
              </pre>
            ) : (
              <div className="text-sm text-muted-foreground animate-pulse">Laster...</div>
            )}
          </CardContent>
        </Card>
      </section>

      {/* Fiken Account Mappings */}
      <section className="space-y-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight">Fiken kontokoder</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Koble linjetyper til Fiken-kontonummer for fakturaer og kreditnotaer. Brukes til bokføring i Fiken.
          </p>
        </div>
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground text-left">
                  <th className="px-6 py-3 font-medium">Linjetype</th>
                  <th className="px-6 py-3 font-medium">Dokumenttype</th>
                  <th className="px-6 py-3 font-medium">Kontonummer</th>
                  <th className="px-6 py-3 font-medium">
                    <div className="flex items-center gap-1.5">
                      MVA-kode
                      <Popover>
                        <PopoverTrigger asChild>
                          <button className="rounded-full w-4 h-4 text-[10px] font-bold bg-muted text-muted-foreground hover:bg-muted/80 flex items-center justify-center leading-none">
                            ?
                          </button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="w-80 text-sm">
                          <p className="font-semibold mb-2">Fiken MVA-koder</p>
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-muted-foreground border-b">
                                <th className="text-left pb-1 font-medium">Kode</th>
                                <th className="text-left pb-1 font-medium">Sats</th>
                                <th className="text-left pb-1 font-medium">Beskrivelse</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {[
                                { code: "high", rate: "25 %", desc: "Standard MVA-sats" },
                                { code: "medium", rate: "15 %", desc: "Matvarer" },
                                { code: "low", rate: "12 %", desc: "Persontransport, kino" },
                                { code: "exempt", rate: "0 %", desc: "MVA-fritatt med fradragsrett" },
                                { code: "exempt_import_export", rate: "0 %", desc: "Import/eksport" },
                                { code: "outside", rate: "—", desc: "Utenfor MVA-loven" },
                                { code: "none", rate: "—", desc: "Ingen MVA / ingen MVA-kode" },
                              ].map(({ code, rate, desc }) => (
                                <tr key={code}>
                                  <td className="py-1 pr-3 font-mono font-medium">{code}</td>
                                  <td className="py-1 pr-3 tabular-nums text-muted-foreground">{rate}</td>
                                  <td className="py-1 text-muted-foreground">{desc}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <p className="mt-3 text-xs text-muted-foreground">La feltet stå tomt for å bruke automatisk logikk basert på beløp.</p>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </th>
                  <th className="px-6 py-3 font-medium w-32"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {mappings === null && (
                  <tr>
                    <td colSpan={5} className="px-6 py-3 text-muted-foreground animate-pulse">Laster...</td>
                  </tr>
                )}
                {mappings?.map((mapping) => (
                  <tr key={mapping.id}>
                    <td className="px-6 py-3 font-medium">
                      {LINE_TYPE_LABELS[mapping.lineType] ?? mapping.lineType}
                    </td>
                    <td className="px-6 py-3 text-muted-foreground">
                      {mapping.lineType === "bank_account" ? "—" : (DOCUMENT_TYPE_LABELS[mapping.documentType] ?? mapping.documentType)}
                    </td>
                    <td className="px-6 py-3">
                      <input
                        className="border rounded px-2 py-1 text-sm w-28 font-mono"
                        value={editValues[mapping.id]?.accountCode ?? mapping.accountCode}
                        onChange={(e) => setEditValues((prev) => ({ ...prev, [mapping.id]: { ...prev[mapping.id], accountCode: e.target.value } }))}
                      />
                    </td>
                    <td className="px-6 py-3">
                      {mapping.lineType !== "bank_account" && (
                        <input
                          className="border rounded px-2 py-1 text-sm w-24 font-mono"
                          placeholder="none"
                          value={editValues[mapping.id]?.vatCode ?? (mapping.vatCode ?? "")}
                          onChange={(e) => setEditValues((prev) => ({ ...prev, [mapping.id]: { ...prev[mapping.id], vatCode: e.target.value } }))}
                        />
                      )}
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex gap-2">
                        <button
                          className="text-xs px-3 py-1 rounded bg-foreground text-background font-medium disabled:opacity-50"
                          disabled={savingId === mapping.id || (editValues[mapping.id]?.accountCode === mapping.accountCode && (editValues[mapping.id]?.vatCode || null) === mapping.vatCode)}
                          onClick={() => saveMapping(mapping.id)}
                        >
                          {savingId === mapping.id ? "Lagrer..." : "Lagre"}
                        </button>
                        <button
                          className="text-xs px-3 py-1 rounded border text-destructive border-destructive/30 hover:bg-destructive/10 font-medium"
                          onClick={() => deleteMapping(mapping.id)}
                        >
                          Slett
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {mappings?.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-3 text-sm text-muted-foreground">
                      Ingen kontokoder registrert ennå.
                    </td>
                  </tr>
                )}
                {/* Add new row */}
                <tr className="bg-muted/30">
                  <td className="px-6 py-3">
                    <select
                      className="border rounded px-2 py-1 text-sm bg-background"
                      value={newMapping.lineType}
                      onChange={(e) => setNewMapping((prev) => ({ ...prev, lineType: e.target.value }))}
                    >
                      {LINE_TYPES.map((t) => (
                        <option key={t} value={t}>{LINE_TYPE_LABELS[t] ?? t}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-6 py-3">
                    {newMapping.lineType === "bank_account" ? (
                      <span className="text-muted-foreground text-sm">—</span>
                    ) : (
                      <select
                        className="border rounded px-2 py-1 text-sm bg-background"
                        value={newMapping.documentType}
                        onChange={(e) => setNewMapping((prev) => ({ ...prev, documentType: e.target.value as DocumentType }))}
                      >
                        {DOCUMENT_TYPES.map((t) => (
                          <option key={t} value={t}>{DOCUMENT_TYPE_LABELS[t]}</option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td className="px-6 py-3">
                    <input
                      className="border rounded px-2 py-1 text-sm w-28 font-mono"
                      placeholder="3000"
                      value={newMapping.accountCode}
                      onChange={(e) => setNewMapping((prev) => ({ ...prev, accountCode: e.target.value }))}
                    />
                  </td>
                  <td className="px-6 py-3">
                    {newMapping.lineType !== "bank_account" && (
                      <input
                        className="border rounded px-2 py-1 text-sm w-24 font-mono"
                        placeholder="none"
                        value={newMapping.vatCode}
                        onChange={(e) => setNewMapping((prev) => ({ ...prev, vatCode: e.target.value }))}
                      />
                    )}
                  </td>
                  <td className="px-6 py-3">
                    <button
                      className="text-xs px-3 py-1 rounded bg-foreground text-background font-medium disabled:opacity-50"
                      disabled={addingNew || !newMapping.accountCode.trim()}
                      onClick={createMapping}
                    >
                      {addingNew ? "Legger til..." : "Legg til"}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
